import { FC } from 'react';
import RefreshOutlined from '@mui/icons-material/RefreshOutlined';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { dailyNavAsOfLabel } from 'common/constants/dailyNav';
import { DAILY_NAV_TOOLTIP_SLOT_PROPS } from 'pages/Intelligence/DailyNav/daily-nav.constants';
import { MarketDataFreshnessProps } from './MarketDataFreshness.props';

const refreshSpin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const FINALIZED_AS_OF_JUSTIFICATION =
  'This day has been finalized. Public comps and secondary prices are read as they existed at the finalize time, so later market revisions cannot change numbers that were already signed off.';

const OPEN_AS_OF_JUSTIFICATION =
  'This day is not finalized. Market data is read as of this report date\'s trading-market session close. Refresh re-queries at that same instant.';

const MarketDataFreshness: FC<MarketDataFreshnessProps> = ({ asOf, isFinalized, isRefreshing, onRefresh }) => {
  const label = dailyNavAsOfLabel(asOf);
  if (!label) {
    return null;
  }

  return (
    <Box display="flex" alignItems="center" gap={0.25}>
      <Tooltip
        arrow
        describeChild
        placement="bottom-start"
        slotProps={DAILY_NAV_TOOLTIP_SLOT_PROPS}
        title={
          <Typography variant="caption" data-testid="daily-nav-market-data-as-of-justification">
            {isFinalized ? FINALIZED_AS_OF_JUSTIFICATION : OPEN_AS_OF_JUSTIFICATION}
          </Typography>
        }>
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="daily-nav-market-data-as-of"
          sx={{ borderBottom: '1px dashed', borderColor: 'text.disabled', cursor: 'help' }}>
          Market data as of {label}
        </Typography>
      </Tooltip>
      {!isFinalized && (
        <Tooltip title="Refresh market data">
          <span>
            <IconButton
              aria-label="Refresh market data"
              data-testid="daily-nav-refresh-market-data"
              disabled={isRefreshing}
              onClick={onRefresh}
              size="small"
              sx={{ p: 0.25 }}>
              <RefreshOutlined
                sx={{
                  fontSize: 14,
                  animation: isRefreshing ? `${refreshSpin} 0.8s linear infinite` : undefined,
                }}
              />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};

export default MarketDataFreshness;
